// CSS imports are handled differently in Metro - using dynamic loading for Expo web
import React, { useEffect, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { de } from "date-fns/locale/de";
import { es } from "date-fns/locale/es";
import { fr } from "date-fns/locale/fr";
import { ja } from "date-fns/locale/ja";
import { nl } from "date-fns/locale/nl";
import { pl } from "date-fns/locale/pl";
import { pt } from "date-fns/locale/pt";
import { ptBR } from "date-fns/locale/pt-BR";
import { createPortal } from "react-dom";
import { Ionicons } from "@expo/vector-icons";
import tw from "twrnc";

import AppInput from "./AppInput";
import { getLanguage, isEnglish, t, tc } from "@/localization";

// Month and day names in the app's language, and labels for screen readers in
// it. English keeps react-datepicker's defaults.
const DATE_FNS_LOCALES = {
  de,
  "es-MX": es,
  "es-ES": es,
  fr,
  ja,
  nl,
  pl,
  "pt-BR": ptBR,
  "pt-PT": pt,
};
const DATE_FNS_LOCALE = DATE_FNS_LOCALES[getLanguage()];
if (DATE_FNS_LOCALE) registerLocale(getLanguage(), DATE_FNS_LOCALE);
const LANGUAGE_PROPS = !isEnglish() && DATE_FNS_LOCALE
  ? {
      locale: getLanguage(),
      chooseDayAriaLabelPrefix: tc("date picker", "Choose"),
      disabledDayAriaLabelPrefix: tc("date picker", "Not available"),
      monthAriaLabelPrefix: tc("date picker", "Month"),
      previousMonthButtonLabel: tc("date picker", "Previous Month"),
      nextMonthButtonLabel: tc("date picker", "Next Month"),
    }
  : {};

// Enhanced CSS loading for Expo web
const loadDatePickerCSS = () => {
  if (typeof document === "undefined") return Promise.resolve();

  return new Promise((resolve) => {
    // Check if CSS is already loaded
    const existingLink = document.querySelector(
      'link[href*="react-datepicker"]'
    );
    if (existingLink) {
      resolve();
      return;
    }

    // Load CSS from CDN since dynamic imports no longer work in Expo Metro
    loadCSSFromCDN().then(resolve);
  });
};

const loadCSSFromCDN = () => {
  return new Promise((resolve) => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.type = "text/css";
    link.href =
      "https://unpkg.com/react-datepicker@8.3.0/dist/react-datepicker.css";

    link.onload = () => resolve();
    link.onerror = () => {
      console.warn("Failed to load react-datepicker CSS from CDN");
      resolve(); // Still resolve to prevent blocking
    };

    document.head.appendChild(link);
  });
};

// react-datepicker 8 renders a screen-reader line ("Selected time: …") that its
// own stylesheet hides; the CDN stylesheet above is v4 and doesn't, so hide it here.
const ensureAriaLiveHidden = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("datepicker-aria-live")) return;
  const style = document.createElement("style");
  style.id = "datepicker-aria-live";
  style.textContent = `.react-datepicker__aria-live{position:absolute;clip-path:circle(0);border:0;height:1px;margin:-1px;overflow:hidden;padding:0;width:1px;white-space:nowrap}.datepicker-chip-wrapper{display:inline-block!important;width:auto!important}`;
  document.head.appendChild(style);
};

// `trigger="chip"`: a pill button instead of a text field (onboarding's date step).
const ChipTrigger = React.forwardRef(
  ({ value, onClick, placeholder, selected }, ref) => (
    <button
      type="button"
      ref={ref}
      onClick={onClick}
      aria-pressed={!!selected}
      style={{
        display: "inline-flex",
        alignItems: "center",
        height: 44,
        padding: "0 16px",
        marginBottom: 8,
        borderRadius: 9999,
        border: `1px solid ${selected ? "#3f5b7a" : "#d1d5db"}`,
        background: selected ? "#3f5b7a" : "#fff",
        color: selected ? "#fff" : "#2E3F52",
        fontWeight: selected ? 700 : 400,
        fontSize: 16,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        cursor: "pointer",
      }}
    >
      <Ionicons
        name="calendar-outline"
        size={16}
        color={selected ? "#fff" : "#3f5b7a"}
        style={{ marginRight: 6 }}
      />
      {value || placeholder}
    </button>
  )
);

// The text field react-datepicker opens on click; its icon opens it too.
const FieldTrigger = React.forwardRef(
  ({ onClick, iconRightName, ...props }, ref) => (
    <AppInput
      {...props}
      onClick={onClick}
      iconRightName={iconRightName}
      onIconRightAction={iconRightName ? onClick : undefined}
    />
  )
);

// Same `mode` / `minimumDate` / `iconName` props as the native picker.
const modeProps = (mode) => {
  if (mode === "date") {
    return { showTimeSelect: false, dateFormat: t("EEEE, MMMM d, yyyy") };
  }
  if (mode === "time") {
    return {
      showTimeSelect: true,
      showTimeSelectOnly: true,
      timeCaption: t("Start time"),
      dateFormat: t("h:mm aa"),
    };
  }
  return {};
};

const AppDatePicker = React.memo(
  ({
    itemProps = [],
    errorMessage,
    mode = "datetime",
    minimumDate,
    iconName,
    trigger,
    ...pickerProps
  }) => {
    const [cssLoaded, setCssLoaded] = useState(false);

    useEffect(() => {
      ensureAriaLiveHidden();
      loadDatePickerCSS().then(() => {
        setCssLoaded(true);
      });
    }, []);

    // Add inline critical styles as fallback
    useEffect(() => {
      if (typeof document !== "undefined" && !cssLoaded) {
        const styleId = "datepicker-fallback-styles";
        if (!document.getElementById(styleId)) {
          const style = document.createElement("style");
          style.id = styleId;
          style.textContent = `
            .react-datepicker-wrapper {
              display: block;
              width: 100%;
            }
            .react-datepicker__input-container {
              position: relative;
              display: inline-block;
              width: 100%;
            }
            .react-datepicker {
              font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
              font-size: 0.8rem;
              background-color: #fff;
              color: #000;
              border: 1px solid #aeaeae;
              border-radius: 0.3rem;
              display: inline-block;
              position: relative;
              z-index: 9999;
            }
            .react-datepicker__month-container {
              float: left;
            }
            .react-datepicker__header {
              text-align: center;
              background-color: #f0f0f0;
              border-bottom: 1px solid #aeaeae;
              border-top-left-radius: 0.3rem;
              border-top-right-radius: 0.3rem;
              padding: 8px 0;
              position: relative;
            }
            .react-datepicker__current-month {
              margin-left: 0;
              color: #000;
              font-weight: bold;
              font-size: 0.944rem;
            }
            .react-datepicker__day-names {
              white-space: nowrap;
              margin: 0;
              padding: 0;
              display: flex;
            }
            .react-datepicker__day-name {
              color: #000;
              display: inline-block;
              width: 1.7rem;
              line-height: 1.7rem;
              text-align: center;
              margin: 0.166rem;
              font-size: 0.75rem;
            }
            .react-datepicker__month {
              margin: 0.4rem;
              text-align: center;
            }
            .react-datepicker__week {
              white-space: nowrap;
              margin: 0;
              padding: 0;
              display: flex;
            }
            .react-datepicker__day {
              color: #000;
              display: inline-block;
              width: 1.7rem;
              line-height: 1.7rem;
              text-align: center;
              margin: 0.166rem;
              cursor: pointer;
              border-radius: 0.3rem;
            }
            .react-datepicker__day:hover {
              background-color: #f0f0f0;
            }
            .react-datepicker__day--selected {
              background-color: #007bff;
              color: #fff;
            }
            .react-datepicker__time-container {
              float: right;
              border-left: 1px solid #aeaeae;
              width: 85px;
            }
            .react-datepicker__time {
              position: relative;
              background: white;
              border-bottom-right-radius: 0.3rem;
            }
            .react-datepicker__time-box {
              width: 85px;
              overflow-x: hidden;
              margin: 0 auto;
              text-align: center;
              border-bottom-right-radius: 0.3rem;
            }
            .react-datepicker__time-list {
              height: 200px;
              overflow-y: scroll;
              margin: 0;
              padding: 0;
            }
            .react-datepicker__time-list-item {
              height: 30px;
              padding: 5px 10px;
              white-space: nowrap;
              font-size: 0.75rem;
              cursor: pointer;
            }
            .react-datepicker__time-list-item:hover {
              background-color: #f0f0f0;
            }
            .react-datepicker__time-list-item--selected {
              background-color: #007bff;
              color: white;
            }
          `;
          document.head.appendChild(style);
        }
      }
    }, [cssLoaded]);

    return (
      <DatePicker
        showTimeSelect
        timeFormat={t("h:mm aa")}
        timeIntervals={15}
        timeCaption={tc("date picker", "time")}
        dateFormat={t("MMMM d, yyyy h:mm aa")}
        popperContainer={({ children }) =>
          createPortal(children, document.body)
        }
        className={tw`m-0 text-base pl-[15px] pr-0 h-[50px] border-0 rounded-[14px] bg-transparent mr-4`}
        customInput={
          trigger === "chip" ? (
            <ChipTrigger selected={!!pickerProps.selected} />
          ) : (
            <FieldTrigger
              disabled={pickerProps.disabled}
              errorMessage={errorMessage}
              iconRightName={iconName}
            />
          )
        }
        {...LANGUAGE_PROPS}
        {...modeProps(mode)}
        {...(trigger === "chip"
          ? {
              dateFormat: t("EEE, MMM d"),
              wrapperClassName: "datepicker-chip-wrapper",
              popperPlacement: "bottom-end",
            }
          : {})}
        {...(minimumDate ? { minDate: minimumDate } : {})}
        {...pickerProps}
      />
    );
  }
);

export default AppDatePicker;
